"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.NOT_READY_RESUBSCRIBE_MS = void 0;
exports.subscribePolymarketUsOrderbook = subscribePolymarketUsOrderbook;
exports.unsubscribePolymarketUsOrderbook = unsubscribePolymarketUsOrderbook;
exports.fetchPolymarketUsOrderbook = fetchPolymarketUsOrderbook;
exports.startPolymarketUsOrderbookPolling = startPolymarketUsOrderbookPolling;
const api_1 = require("./api");
const auth_1 = require("./auth");
const MAX_ORDERBOOK_FETCH_ATTEMPTS = 20; // Default cap (~20s with 1s polling).
// Give the server a moment to establish the WS subscription before polling.
const INITIAL_POLLING_DELAY_MS = 1000;
/**
 * Still no book this long after subscribing: subscribe again. A stale
 * unsubscribe can land after a fresh subscribe for the same market on fast
 * ‹ › taps; a repeat subscribe is a no-op server-side.
 */
exports.NOT_READY_RESUBSCRIBE_MS = 3000;
/**
 * Subscribe to Polymarket US orderbook for given market slugs.
 */
async function subscribePolymarketUsOrderbook(marketSlugs) {
    try {
        const sessionToken = (0, auth_1.getSessionToken)();
        if (!sessionToken) {
            throw new Error("Not authenticated. Please login.");
        }
        const response = await fetch(`${(0, api_1.getApiBaseUrl)()}/polymarket-us/orderbook/subscribe`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-Session-Token": sessionToken,
            },
            body: JSON.stringify({ marketSlugs }),
        });
        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Failed to subscribe to Polymarket US orderbook: ${errorText}`);
        }
    }
    catch (error) {
        console.error("Error subscribing to Polymarket US orderbook:", error);
        throw error;
    }
}
/**
 * Unsubscribe from Polymarket US orderbook for given market slugs.
 */
async function unsubscribePolymarketUsOrderbook(marketSlugs) {
    try {
        const sessionToken = (0, auth_1.getSessionToken)();
        if (!sessionToken) {
            console.warn("No session token for unsubscribe");
            return;
        }
        const response = await fetch(`${(0, api_1.getApiBaseUrl)()}/polymarket-us/orderbook/unsubscribe`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-Session-Token": sessionToken,
            },
            body: JSON.stringify({ marketSlugs }),
        });
        if (!response.ok) {
            console.error("Failed to unsubscribe from Polymarket US orderbook");
        }
    }
    catch (error) {
        console.error("Error unsubscribing from Polymarket US orderbook:", error);
    }
}
/**
 * Fetch the pre-bucketed display orderbook from the server cache.
 */
async function fetchPolymarketUsOrderbook(marketSlug) {
    try {
        const sessionToken = (0, auth_1.getSessionToken)();
        if (!sessionToken) {
            throw new Error("Not authenticated. Please login.");
        }
        const response = await fetch(`${(0, api_1.getApiBaseUrl)()}/polymarket-us/orderbook?marketSlug=${encodeURIComponent(marketSlug)}&format=display`, {
            headers: {
                "X-Session-Token": sessionToken,
            },
        });
        if (!response.ok) {
            throw new Error(`Failed to fetch Polymarket US orderbook: ${response.statusText}`);
        }
        const parsed = (await response.json());
        return parsed?.display ? parsed : null;
    }
    catch (error) {
        console.error("Error fetching Polymarket US orderbook:", error);
        return null;
    }
}
/**
 * Start polling Polymarket US orderbook updates.
 */
function startPolymarketUsOrderbookPolling(marketSlug, callback, intervalMs = 1000) {
    let intervalId = null;
    let timeoutId = null;
    let isSubscribed = false;
    let isCleaned = false;
    let attemptCount = 0;
    let subscribedAt = 0;
    const maxAttempts = MAX_ORDERBOOK_FETCH_ATTEMPTS;
    subscribePolymarketUsOrderbook([marketSlug])
        .then(() => {
        if (isCleaned) {
            unsubscribePolymarketUsOrderbook([marketSlug]).catch(console.error);
            return;
        }
        isSubscribed = true;
        subscribedAt = Date.now();
        timeoutId = window.setTimeout(() => {
            if (isCleaned) {
                return;
            }
            intervalId = window.setInterval(async () => {
                if (isCleaned) {
                    return;
                }
                attemptCount++;
                const orderbook = await fetchPolymarketUsOrderbook(marketSlug);
                if (isCleaned) {
                    return;
                }
                if (!orderbook && Date.now() - subscribedAt >= exports.NOT_READY_RESUBSCRIBE_MS) {
                    subscribedAt = Date.now();
                    subscribePolymarketUsOrderbook([marketSlug]).catch(() => undefined);
                }
                if (orderbook) {
                    callback(orderbook);
                    attemptCount = 0;
                }
                else if (attemptCount > maxAttempts) {
                    console.warn(`⚠️ No PM-US orderbook data after ${maxAttempts} attempts.`);
                }
            }, intervalMs);
        }, INITIAL_POLLING_DELAY_MS);
    })
        .catch((error) => {
        console.error("Failed to start Polymarket US orderbook polling:", error);
    });
    return () => {
        isCleaned = true;
        if (timeoutId !== null) {
            clearTimeout(timeoutId);
            timeoutId = null;
        }
        if (intervalId !== null) {
            clearInterval(intervalId);
            intervalId = null;
        }
        if (isSubscribed) {
            unsubscribePolymarketUsOrderbook([marketSlug]).catch(console.error);
        }
    };
}
